import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { missionService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import AssignmentManager from '../components/AssignmentManager';
import BlocatgeCopia from '../components/BlocatgeCopia';
import MultilingualTextInput from '../components/MultilingualTextInput';
import MultilingualTextArea from '../components/MultilingualTextArea';
import { useLocalizedContent, getLocalizedContent } from '../utils/language';
import { mostraDataHora, aInputLocal, aUTCDelServidor } from '../utils/dates';
import ReactMarkdown from 'react-markdown';

function MissionDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const getLocalizedText = useLocalizedContent();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHypothesisForm, setShowHypothesisForm] = useState(false);
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [useCustomHCode, setUseCustomHCode] = useState(false);
  const [useCustomECode, setUseCustomECode] = useState(false);
  const [hypothesisForm, setHypothesisForm] = useState({
    code: '',
    short_description: { en: '', ca: '' },
    description: { en: '', ca: '' },
    order_position: 1
  });
  const [evidenceForm, setEvidenceForm] = useState({
    code: '',
    short_description: { en: '', ca: '' },
    description: { en: '', ca: '' },
    order_position: 1,
    source: { en: '', ca: '' },
    reliability: null
  });
  const [editingTimeLimit, setEditingTimeLimit] = useState(false);
  const [timeLimitForm, setTimeLimitForm] = useState(60);
  const [editingHypothesis, setEditingHypothesis] = useState(null);
  const [editingEvidence, setEditingEvidence] = useState(null);
  const [editingStory, setEditingStory] = useState(false);
  const [storyForm, setStoryForm] = useState({ en: '', ca: '' });
  const [showAssignmentManager, setShowAssignmentManager] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState(null);
  const [editingWindow, setEditingWindow] = useState(false);
  const [windowForm, setWindowForm] = useState({ start: '', end: '' });
  const { isInstructor } = useAuth();

  useEffect(() => {
    fetchMission();
  }, [id]);

  const fetchMission = async () => {
    try {
      // Contingut sense traduir: aquesta pantalla edita les dues versions alhora.
      // Amb la versió traduïda, `story` arribava com a cadena, la comprovació de
      // sota fallava sempre i el formulari es reiniciava buit: desar el briefing
      // en un idioma esborrava el de l'altre.
      const response = await missionService.getMissionForEditing(id);
      setMission(response.data);
      setTimeLimitForm(response.data.time_limit_minutes);
      const story = response.data.story;
      if (story && typeof story === 'object') {
        setStoryForm({
          en: story.en || '',
          ca: story.ca || ''
        });
      } else {
        setStoryForm({ en: '', ca: '' });
      }
    } catch (error) {
      toast.error(t('missionDetail.failedToFetchMissionDetails'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddHypothesis = async (e) => {
    e.preventDefault();
    try {
      // Calculate next order position
      const nextPosition = (mission.hypotheses?.length || 0) + 1;
      const code = useCustomHCode && hypothesisForm.code ? hypothesisForm.code : `H${nextPosition}`;
      const formData = {
        ...hypothesisForm,
        code: code,
        order_position: nextPosition,
        mission_id: parseInt(id)
      };

      await missionService.addHypothesis(id, formData);
      toast.success(t('missionDetail.hypothesisAddedSuccess'));
      setHypothesisForm({
        code: '',
        short_description: { en: '', ca: '' },
        description: { en: '', ca: '' },
        order_position: 1
      });
      setShowHypothesisForm(false);
      setUseCustomHCode(false);
      fetchMission(); // Refresh mission data
    } catch (error) {
      let message = t('missionDetail.failedToAddHypothesis');
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
    }
  };

  const handleAddEvidence = async (e) => {
    e.preventDefault();
    try {
      // Calculate next order position
      const nextPosition = (mission.evidence?.length || 0) + 1;
      const code = useCustomECode && evidenceForm.code ? evidenceForm.code : `E${nextPosition}`;
      const formData = {
        ...evidenceForm,
        code: code,
        order_position: nextPosition,
        mission_id: parseInt(id),
        reliability: evidenceForm.reliability ? parseFloat(evidenceForm.reliability) : null
      };

      await missionService.addEvidence(id, formData);
      toast.success(t('missionDetail.evidenceAddedSuccess'));
      setEvidenceForm({
        code: '',
        short_description: { en: '', ca: '' },
        description: { en: '', ca: '' },
        order_position: 1,
        source: { en: '', ca: '' },
        reliability: null
      });
      setShowEvidenceForm(false);
      setUseCustomECode(false);
      fetchMission(); // Refresh mission data
    } catch (error) {
      let message = t('missionDetail.failedToAddEvidence');
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
    }
  };

  const handleUpdateTimeLimit = async () => {
    try {
      await missionService.updateMission(id, { time_limit_minutes: timeLimitForm });
      toast.success(t('missionDetail.timeLimitUpdatedSuccess'));
      setEditingTimeLimit(false);
      fetchMission();
    } catch (error) {
      toast.error(t('missionDetail.failedToUpdateTimeLimit'));
    }
  };

  // Desar la finestra. Els camps buits s'envien com a null a posta: és
  // l'única manera de TREURE una finestra que ja no es vol, i sense això la
  // missió quedaria tancada per sempre a partir de la data que hi hagués.
  const handleUpdateWindow = async () => {
    try {
      await missionService.updateMission(id, {
        start_time: aUTCDelServidor(windowForm.start),
        end_time: aUTCDelServidor(windowForm.end),
      });
      toast.success(t('missionDetail.windowUpdatedSuccess'));
      setEditingWindow(false);
      fetchMission();
    } catch (error) {
      const detall = error.response?.data?.detail;
      toast.error(typeof detall === 'string' ? detall : t('missionDetail.failedToUpdateWindow'));
    }
  };

  const handleDeleteHypothesis = async (hypothesisId) => {
    if (!window.confirm(t('missionDetail.confirmDeleteHypothesis'))) {
      return;
    }
    try {
      await missionService.deleteHypothesis(id, hypothesisId);
      toast.success(t('missionDetail.hypothesisDeletedSuccess'));
      fetchMission();
    } catch (error) {
      toast.error(t('missionDetail.failedToDeleteHypothesis'));
    }
  };

  const handleDeleteEvidence = async (evidenceId) => {
    if (!window.confirm(t('missionDetail.confirmDeleteEvidence'))) {
      return;
    }
    try {
      await missionService.deleteEvidence(id, evidenceId);
      toast.success(t('missionDetail.evidenceDeletedSuccess'));
      fetchMission();
    } catch (error) {
      toast.error(t('missionDetail.failedToDeleteEvidence'));
    }
  };

  const handleUpdateHypothesis = async () => {
    try {
      await missionService.updateHypothesis(id, editingHypothesis.id, {
        code: editingHypothesis.code,
        short_description: editingHypothesis.short_description || '',
        description: editingHypothesis.description,
        order_position: editingHypothesis.order_position,
        mission_id: parseInt(id)
      });
      toast.success(t('missionDetail.hypothesisUpdatedSuccess'));
      setEditingHypothesis(null);
      fetchMission();
    } catch (error) {
      toast.error(t('missionDetail.failedToUpdateHypothesis'));
    }
  };

  const handleUpdateEvidence = async () => {
    try {
      await missionService.updateEvidence(id, editingEvidence.id, {
        code: editingEvidence.code,
        short_description: editingEvidence.short_description || { en: '', ca: '' },
        description: editingEvidence.description,
        order_position: editingEvidence.order_position,
        source: editingEvidence.source || { en: '', ca: '' },
        reliability: editingEvidence.reliability ? parseFloat(editingEvidence.reliability) : null,
        mission_id: parseInt(id)
      });
      toast.success(t('missionDetail.evidenceUpdatedSuccess'));
      setEditingEvidence(null);
      fetchMission();
    } catch (error) {
      toast.error(t('missionDetail.failedToUpdateEvidence'));
    }
  };


  const handleUpdateStory = async () => {
    try {
      await missionService.updateMission(id, { story: storyForm });
      toast.success(t('missionDetail.missionBriefingUpdatedSuccess'));
      setEditingStory(false);
      fetchMission();
    } catch (error) {
      toast.error(t('missionDetail.failedToUpdateMissionBriefing'));
    }
  };

  const handleAssignmentsUpdated = () => {
    // Refresh mission data to update assignment count
    fetchMission();
  };

  const showDescriptionModal = (code, description, type, shortDescription) => {
    setDescriptionModal({
      code,
      description,
      type,
      shortDescription
    });
  };

  // El carril de l'instructor s'eixampla a tot l'ample mentre hi hagi un
  // formulari obert. A 30rem els camps d'edició —codi, dues descripcions, font
  // i fiabilitat— no hi caben, i estrènyer-li el formulari seria empitjorar-li
  // la feina: el carril val per LLEGIR el material, no per escriure'l.
  const editantMaterial = Boolean(
    showHypothesisForm || showEvidenceForm || editingHypothesis || editingEvidence || editingStory
  );

  if (loading) return <div className="loading">{t('missionDetail.loadingMissionDetails')}</div>;
  if (!mission) return <div>{t('missionDetail.missionNotFound')}</div>;

  return (
    <div>
      {/* L'instructor hi enganxa els relats i n'ha de poder copiar el text:
          el bloqueig és només per a l'alumne. */}
      <BlocatgeCopia actiu={!isInstructor} />

      <div className="mission-header">
        <h1>{getLocalizedContent(mission.title, i18n.language)}</h1>
        <div className="mission-meta">
          <span className={`badge badge-${mission.is_active ? 'success' : 'warning'}`}>
            {mission.is_active ? t('common.active') : t('common.inactive')}
          </span>
          {editingTimeLimit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <input
                type="number"
                value={timeLimitForm}
                onChange={(e) => setTimeLimitForm(parseInt(e.target.value))}
                min="1"
                style={{ width: '80px', padding: '4px 8px', fontSize: '0.875rem' }}
              />
              <span>{t('missionDetail.minutes')}</span>
              <button onClick={handleUpdateTimeLimit} className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                {t('missionDetail.save')}
              </button>
              <button onClick={() => setEditingTimeLimit(false)} className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                {t('missionDetail.cancel')}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <span className="badge badge-secondary">
                {mission.time_limit_minutes} {t('missionDetail.minutes')}
              </span>
              {isInstructor && (
                <button
                  onClick={() => setEditingTimeLimit(true)}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                >
                  {t('missionDetail.edit')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Finestra de la missió: quan es pot començar i quan es tanca per a
            tothom. Buida vol dir sempre oberta, que és com ha funcionat sempre
            l'aplicació. Les hores són les d'aquí; el servidor treballa en UTC. */}
        {isInstructor && !editingWindow && (
          <div className="mission-window">
            <span className="badge badge-secondary">
              {mission.start_time || mission.end_time
                ? `${mostraDataHora(mission.start_time, t('missionDetail.windowNoStart'))} → ${mostraDataHora(mission.end_time, t('missionDetail.windowNoEnd'))}`
                : t('missionDetail.windowAlwaysOpen')}
            </span>
            <button
              onClick={() => {
                setWindowForm({
                  start: aInputLocal(mission.start_time),
                  end: aInputLocal(mission.end_time),
                });
                setEditingWindow(true);
              }}
              className="btn btn-secondary btn-petit"
            >
              {t('missionDetail.editWindow')}
            </button>
          </div>
        )}
      </div>

      {/* L'edició de la finestra va a l'ample i no dins de la capçalera: allà
          era una columna estreta i els dos camps de data, els dos botons i
          l'explicació queien un sota l'altre, centrats. */}
      {isInstructor && editingWindow && (
        <div className="panell-finestra">
          <div className="panell-finestra-camps">
            <div className="form-group">
              <label htmlFor="finestra-obre">{t('missionDetail.windowOpens')}</label>
              <input
                id="finestra-obre"
                type="datetime-local"
                value={windowForm.start}
                onChange={(e) => setWindowForm(prev => ({ ...prev, start: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="finestra-tanca">{t('missionDetail.windowCloses')}</label>
              <input
                id="finestra-tanca"
                type="datetime-local"
                value={windowForm.end}
                onChange={(e) => setWindowForm(prev => ({ ...prev, end: e.target.value }))}
              />
            </div>
            <div className="panell-finestra-accions">
              <button onClick={handleUpdateWindow} className="btn btn-primary btn-petit">
                {t('missionDetail.save')}
              </button>
              <button onClick={() => setEditingWindow(false)} className="btn btn-secondary btn-petit">
                {t('missionDetail.cancel')}
              </button>
            </div>
          </div>
          <p className="camp-ajuda">{t('missionDetail.windowHint')}</p>
        </div>
      )}

      {/* L'acció, a dalt.
          Era al final d'una pàgina de nou mil píxels: per començar l'exercici,
          l'alumne havia de passar per davant de tot el contingut. Qui ja sap
          què vol fer no hi ha de fer nou pantalles de camí. */}
      <div className="missio-accions">
        {(mission.hypotheses?.length > 0 && mission.evidence?.length > 0) ? (
          <>
            {!isInstructor && (
              <Link to={`/matrix/${id}`} className="btn btn-primary">
                {t('missionDetail.startACHAnalysis')}
              </Link>
            )}
            {isInstructor && (
              <>
                <button onClick={() => setShowAssignmentManager(true)} className="btn btn-accent">
                  {t('missionDetail.manageAssignments')}
                </button>
                <Link to={`/matrix/${id}`} className="btn btn-secondary">
                  {t('missionDetail.previewAnalysis')}
                </Link>
                <Link to={`/missions/${id}/submissions`} className="btn btn-primary">
                  {t('missionDetail.viewSubmissions')}
                </Link>
              </>
            )}
          </>
        ) : (
          <p className="text-muted">
            {isInstructor ? t('missionDetail.addHypothesesEvidenceToEnable') : t('missionDetail.analysisNotYetAvailable')}
          </p>
        )}
      </div>

      {/* L'alumne llegeix el cas AMB les hipòtesis i les evidències al
          costat, que és com es treballa; no després de tot el relat. A
          l'instructor no se li estreny res: aquestes dues columnes són el seu
          formulari d'edició i necessiten l'amplada sencera. */}
      <div className={`pantalla-missio${isInstructor
        ? ` pantalla-missio--instructor${editantMaterial ? ' pantalla-missio--editant' : ''}`
        : ' pantalla-missio--alumne'}`}>
      <div className="mission-story">
        <div className="story-header">
          <h2>{t('missionDetail.missionBriefing')}</h2>
          {isInstructor && !editingStory && (
            <button
              onClick={() => setEditingStory(true)}
              className="text-link"
            >
              {t('missionDetail.edit')}
            </button>
          )}
        </div>
        {editingStory ? (
          <div className="edit-section">
            <MultilingualTextArea
              label=""
              value={storyForm}
              onChange={(value) => setStoryForm(value)}
              placeholder={{
                en: t('missionDetail.enterMissionBriefing'),
                ca: "Introdueix el briefing/història de la missió..."
              }}
              rows={8}
              required
            />
            <div className="text-link-buttons">
              <button onClick={handleUpdateStory} className="text-link text-link-primary">
                {t('missionDetail.save')}
              </button>
              <button onClick={() => setEditingStory(false)} className="text-link text-link-secondary">
                {t('missionDetail.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="story-content">
            <ReactMarkdown>
              {getLocalizedContent(mission.story, i18n.language) || getLocalizedContent(mission.description, i18n.language) || t('missionDetail.noMissionBriefingProvided')}
            </ReactMarkdown>
          </div>
        )}
      </div>

      <div className="analysis-sections">
        {!isInstructor && (
          <h2 className="carril-titol">{t('missionDetail.whatYouWillGet')}</h2>
        )}
        <div className="section-hypotheses">
          <div className="section-header">
            <h2>{t('missionDetail.hypotheses')} <span className="count">({mission.hypotheses?.length || 0})</span></h2>
            {isInstructor && (
              <button
                className="btn btn-secondary"
                onClick={() => setShowHypothesisForm(true)}
              >
                + {t('missionDetail.add')}
              </button>
            )}
          </div>

          {mission.hypotheses?.length > 0 ? (
            <div className="items-list">
              {mission.hypotheses.map((h) => (
                <div key={h.id} className="list-item">
                  {editingHypothesis?.id === h.id ? (
                    <>
                      <input
                        type="text"
                        value={editingHypothesis.code}
                        onChange={(e) => setEditingHypothesis({...editingHypothesis, code: e.target.value})}
                        className="edit-input-small"
                      />
                      <div className="edit-descriptions">
                        <MultilingualTextInput
                          label=""
                          value={editingHypothesis.short_description || { en: '', ca: '' }}
                          onChange={(value) => setEditingHypothesis({...editingHypothesis, short_description: value})}
                          placeholder={{
                            en: "Short description (max 100 chars)",
                            ca: "Descripció breu (màx 100 caràcters)"
                          }}
                          maxLength={100}
                          compact={true}
                        />
                        <MultilingualTextArea
                          label=""
                          value={editingHypothesis.description || { en: '', ca: '' }}
                          onChange={(value) => setEditingHypothesis({...editingHypothesis, description: value})}
                          placeholder={{
                            en: "Full description",
                            ca: "Descripció completa"
                          }}
                          rows={3}
                          compact={true}
                        />
                      </div>
                      <div className="edit-buttons">
                        <button onClick={handleUpdateHypothesis} className="save-btn" title={t('missionDetail.save')}>{t('missionDetail.save')}</button>
                        <button onClick={() => setEditingHypothesis(null)} className="cancel-btn" title={t('missionDetail.cancel')}>×</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="item-badge hypothesis-badge">{h.code}</span>
                      <div className="item-content">
                        <span className="item-text">{getLocalizedContent(h.short_description, i18n.language) || getLocalizedContent(h.description, i18n.language)}</span>
                      </div>
                      <div className="action-buttons">
                        {h.short_description && h.description && (
                          <button
                            className="info-btn"
                            onClick={() => showDescriptionModal(h.code, getLocalizedContent(h.description, i18n.language), t('matrix.hypothesis'), getLocalizedContent(h.short_description, i18n.language))}
                            title={t('missionDetail.viewFullDescription')}
                          >
                            {t('matrix.more')}
                          </button>
                        )}
                        {isInstructor && (
                          <>
                            <button
                              className="edit-btn"
                              onClick={() => setEditingHypothesis(h)}
                              title={t('missionDetail.editHypothesis')}
                            >
                              {t('missionDetail.edit')}
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteHypothesis(h.id)}
                              title={t('missionDetail.deleteHypothesis')}
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>{t('missionDetail.noHypothesesDefined')}</p>
              {isInstructor && <p className="text-muted">{t('missionDetail.addHypothesesEvidence')}</p>}
            </div>
          )}

          {showHypothesisForm && (
            <div className="form-inline">
              <form onSubmit={handleAddHypothesis}>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={useCustomHCode}
                      onChange={(e) => setUseCustomHCode(e.target.checked)}
                    />
                    {t('missionDetail.useCustomCode')}
                  </label>
                  {useCustomHCode && (
                    <input
                      type="text"
                      value={hypothesisForm.code}
                      onChange={(e) => setHypothesisForm({...hypothesisForm, code: e.target.value})}
                      placeholder={t('missionDetail.enterCustomCode')}
                      style={{ marginTop: 'var(--spacing-sm)' }}
                    />
                  )}
                  {!useCustomHCode && (
                    <small className="text-muted">{t('missionDetail.willUse')} H{(mission.hypotheses?.length || 0) + 1}</small>
                  )}
                </div>
                <div className="form-group">
                  <MultilingualTextInput
                    label={t('missionDetail.shortDescription')}
                    value={hypothesisForm.short_description}
                    onChange={(value) => setHypothesisForm({...hypothesisForm, short_description: value})}
                    placeholder={{
                      en: t('missionDetail.briefSummary'),
                      ca: "Resum breu (màx 100 caràcters)"
                    }}
                    maxLength={100}
                    required
                  />
                </div>
                <div className="form-group">
                  <MultilingualTextArea
                    label={t('missionDetail.fullDescription')}
                    value={hypothesisForm.description}
                    onChange={(value) => setHypothesisForm({...hypothesisForm, description: value})}
                    placeholder={{
                      en: t('missionDetail.detailedDescriptionHypothesis'),
                      ca: "Descripció detallada de la hipòtesi..."
                    }}
                    rows={4}
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <button type="submit" className="btn btn-primary" style={{ fontSize: '0.75rem' }}>
                    {t('missionDetail.add')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowHypothesisForm(false);
                      setHypothesisForm({
                        code: '',
                        short_description: { en: '', ca: '' },
                        description: { en: '', ca: '' },
                        order_position: 1
                      });
                      setUseCustomHCode(false);
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {t('missionDetail.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="section-evidence">
          <div className="section-header">
            <h2>{t('missionDetail.evidence')} <span className="count">({mission.evidence?.length || 0})</span></h2>
            {isInstructor && (
              <button
                className="btn btn-secondary"
                onClick={() => setShowEvidenceForm(true)}
              >
                + {t('missionDetail.add')}
              </button>
            )}
          </div>

          {mission.evidence?.length > 0 ? (
            <div className="items-list">
              {mission.evidence.map((e) => (
                <div key={e.id} className="list-item">
                  {editingEvidence?.id === e.id ? (
                    <>
                      <input
                        type="text"
                        value={editingEvidence.code}
                        onChange={(e) => setEditingEvidence({...editingEvidence, code: e.target.value})}
                        className="edit-input-small"
                      />
                      <div className="edit-evidence-content">
                        <MultilingualTextInput
                          label=""
                          value={editingEvidence.short_description || { en: '', ca: '' }}
                          onChange={(value) => setEditingEvidence({...editingEvidence, short_description: value})}
                          placeholder={{
                            en: "Short description (max 100 chars)",
                            ca: "Descripció breu (màx 100 caràcters)"
                          }}
                          maxLength={100}
                          compact={true}
                        />
                        <MultilingualTextArea
                          label=""
                          value={editingEvidence.description || { en: '', ca: '' }}
                          onChange={(value) => setEditingEvidence({...editingEvidence, description: value})}
                          placeholder={{
                            en: "Full description",
                            ca: "Descripció completa"
                          }}
                          rows={3}
                          compact={true}
                        />
                        <MultilingualTextInput
                          label=""
                          value={editingEvidence.source || { en: '', ca: '' }}
                          onChange={(value) => setEditingEvidence({...editingEvidence, source: value})}
                          placeholder={{ en: 'Source', ca: 'Font' }}
                          compact={true}
                        />
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="1"
                          value={editingEvidence.reliability || ''}
                          onChange={(e) => setEditingEvidence({...editingEvidence, reliability: e.target.value})}
                          className="edit-input-small"
                          placeholder="Reliability"
                        />
                      </div>
                      <div className="edit-buttons">
                        <button onClick={handleUpdateEvidence} className="save-btn" title={t('missionDetail.save')}>{t('missionDetail.save')}</button>
                        <button onClick={() => setEditingEvidence(null)} className="cancel-btn" title={t('missionDetail.cancel')}>×</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="item-badge evidence-badge">{e.code}</span>
                      <div className="item-content">
                        <span className="item-text">{getLocalizedContent(e.short_description, i18n.language) || getLocalizedContent(e.description, i18n.language)}</span>
                        <div className="item-metadata">
                          {e.source && (
                            <span className="metadata-item">
                              {t('matrix.source')}: {getLocalizedContent(e.source, i18n.language)}
                            </span>
                          )}
                          {e.reliability && (
                            <span className="metadata-item">
                              {t('matrix.reliability')}: {e.reliability}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="action-buttons">
                        {e.short_description && e.description && (
                          <button
                            className="info-btn"
                            onClick={() => showDescriptionModal(e.code, getLocalizedContent(e.description, i18n.language), t('matrix.evidence'), getLocalizedContent(e.short_description, i18n.language))}
                            title={t('missionDetail.viewFullDescription')}
                          >
                            {t('matrix.more')}
                          </button>
                        )}
                        {isInstructor && (
                          <>
                            <button
                              className="edit-btn"
                              onClick={() => setEditingEvidence(e)}
                              title={t('missionDetail.editEvidence')}
                            >
                              {t('missionDetail.edit')}
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteEvidence(e.id)}
                              title={t('missionDetail.deleteEvidence')}
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>{t('missionDetail.noEvidenceDefined')}</p>
              {isInstructor && <p className="text-muted">{t('missionDetail.addEvidenceToEnable')}</p>}
            </div>
          )}

          {showEvidenceForm && (
            <div className="form-inline">
              <form onSubmit={handleAddEvidence}>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={useCustomECode}
                      onChange={(e) => setUseCustomECode(e.target.checked)}
                    />
                    {t('missionDetail.useCustomCode')}
                  </label>
                  {useCustomECode && (
                    <input
                      type="text"
                      value={evidenceForm.code}
                      onChange={(e) => setEvidenceForm({...evidenceForm, code: e.target.value})}
                      placeholder={t('missionDetail.enterCustomCode')}
                      style={{ marginTop: 'var(--spacing-sm)' }}
                    />
                  )}
                  {!useCustomECode && (
                    <small className="text-muted">{t('missionDetail.willUse')} E{(mission.evidence?.length || 0) + 1}</small>
                  )}
                </div>
                <div className="form-group">
                  <MultilingualTextInput
                    label={t('missionDetail.shortDescription')}
                    value={evidenceForm.short_description}
                    onChange={(value) => setEvidenceForm({...evidenceForm, short_description: value})}
                    placeholder={{
                      en: t('missionDetail.briefSummary'),
                      ca: "Resum breu (màx 100 caràcters)"
                    }}
                    maxLength={100}
                    required
                  />
                </div>
                <div className="form-group">
                  <MultilingualTextArea
                    label={t('missionDetail.fullDescription')}
                    value={evidenceForm.description}
                    onChange={(value) => setEvidenceForm({...evidenceForm, description: value})}
                    placeholder={{
                      en: t('missionDetail.detailedDescriptionEvidence'),
                      ca: "Descripció detallada de l'evidència..."
                    }}
                    rows={4}
                    required
                  />
                </div>
                <div className="form-group">
                  <MultilingualTextInput
                    label={t('missionDetail.source')}
                    value={evidenceForm.source}
                    onChange={(value) => setEvidenceForm({...evidenceForm, source: value})}
                    placeholder={{ en: 'Source of evidence', ca: 'Font de l\'evidència' }}
                  />
                </div>
                <div className="form-group">
                  <label>{t('missionDetail.reliability')}</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={evidenceForm.reliability || ''}
                    onChange={(e) => setEvidenceForm({...evidenceForm, reliability: e.target.value})}
                    placeholder={t('missionDetail.reliabilityRange')}
                  />
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <button type="submit" className="btn btn-primary" style={{ fontSize: '0.75rem' }}>
                    {t('missionDetail.add')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEvidenceForm(false);
                      setEvidenceForm({
        code: '',
        short_description: { en: '', ca: '' },
        description: { en: '', ca: '' },
        order_position: 1,
        source: { en: '', ca: '' },
        reliability: null
      });
                      setUseCustomECode(false);
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {t('missionDetail.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Description Modal */}
      {descriptionModal && (
        <div className="modal-overlay" onClick={() => setDescriptionModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{descriptionModal.code} - {descriptionModal.shortDescription}</h3>
              <button className="modal-close" onClick={() => setDescriptionModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>{descriptionModal.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* La finestra d'assignacions.
          El títol se li passa RESOLT, no en cru: aquesta pantalla demana el
          contingut multilingüe sencer per poder-lo editar, i un objecte
          {'{en, ca}'} escrit al JSX tomba tot React. */}
      <AssignmentManager
        missionId={parseInt(id)}
        missionTitle={getLocalizedContent(mission.title, i18n.language)}
        isOpen={showAssignmentManager}
        onClose={() => setShowAssignmentManager(false)}
        onAssignmentsUpdated={handleAssignmentsUpdated}
      />
    </div>
  );
}

export default MissionDetail;